import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { GpsAreaBounds } from './WorkAreaSection'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Eraser, SquareDashedMousePointer } from 'lucide-react'
import { Button } from 'shared/ui/button'

type MapViewMode = 'street' | 'satellite'

interface GpsPickerMapProps {
  latitude?: number | null
  longitude?: number | null
  fallbackLatitude?: number | null
  fallbackLongitude?: number | null
  canInteract?: boolean
  gpsAreas?: GpsAreaBounds[] | null
  markerLabel?: string | null
  instructionText?: string | null
  onChange?: (payload: {
    latitude: number
    longitude: number
    gpsAreas: GpsAreaBounds[]
  }) => void
}

const DEFAULT_CENTER: [number, number] = [12.658763, 101.310768]
const DEFAULT_ZOOM = 18
const DEFAULT_COORDINATES = {
  latitude: DEFAULT_CENTER[0],
  longitude: DEFAULT_CENTER[1],
}

const FINAL_RECTANGLE_STYLE: L.PathOptions = {
  color: '#2563eb',
  weight: 2,
  fillColor: '#3b82f6',
  fillOpacity: 0.16,
}

const DRAFT_RECTANGLE_STYLE: L.PathOptions = {
  color: '#60a5fa',
  weight: 2,
  dashArray: '6 4',
  fillColor: '#93c5fd',
  fillOpacity: 0.12,
}

const createMarkerIcon = () =>
  L.divIcon({
    className: 'gps-picker-pin',
    html: `
      <div style="display:flex;align-items:center;justify-content:center;">
        <div style="width:18px;height:18px;border-radius:9999px;background:#dc2626;border:3px solid #ffffff;box-shadow:0 4px 10px rgba(0,0,0,0.35);"></div>
      </div>
    `,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  })

const hasCoordinate = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value)

const toBoundsObject = (bounds: L.LatLngBounds): GpsAreaBounds => {
  const northEast = bounds.getNorthEast()
  const southWest = bounds.getSouthWest()
  return {
    north: Number(northEast.lat.toFixed(6)),
    south: Number(southWest.lat.toFixed(6)),
    east: Number(northEast.lng.toFixed(6)),
    west: Number(southWest.lng.toFixed(6)),
  }
}

const fromBoundsObject = (area: GpsAreaBounds) =>
  L.latLngBounds(L.latLng(area.south, area.west), L.latLng(area.north, area.east))

const isTinyBounds = (bounds: L.LatLngBounds) => {
  const northEast = bounds.getNorthEast()
  const southWest = bounds.getSouthWest()
  return Math.abs(northEast.lat - southWest.lat) < 0.00001 && Math.abs(northEast.lng - southWest.lng) < 0.00001
}

export function GpsPickerMap({
  latitude,
  longitude,
  fallbackLatitude,
  fallbackLongitude,
  canInteract = true,
  gpsAreas,
  markerLabel,
  instructionText,
  onChange,
}: GpsPickerMapProps) {
  const mapElementRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<L.Map | null>(null)
  const markerRef = useRef<L.Marker | null>(null)
  const layerRef = useRef<L.TileLayer | null>(null)
  const draftRectangleRef = useRef<L.Rectangle | null>(null)
  const committedRectanglesRef = useRef<L.Rectangle[]>([])
  const dragStartRef = useRef<L.LatLng | null>(null)
  const canInteractRef = useRef(canInteract)
  const selectModeRef = useRef(false)
  const onChangeRef = useRef(onChange)
  const [mapViewMode, setMapViewMode] = useState<MapViewMode>('satellite')
  const [isSelectMode, setIsSelectMode] = useState(false)

  const resolvedCoordinates = useMemo(() => {
    const lat = hasCoordinate(latitude)
      ? latitude
      : hasCoordinate(fallbackLatitude)
        ? fallbackLatitude
        : DEFAULT_COORDINATES.latitude
    const lng = hasCoordinate(longitude)
      ? longitude
      : hasCoordinate(fallbackLongitude)
        ? fallbackLongitude
        : DEFAULT_COORDINATES.longitude

    return { latitude: lat, longitude: lng }
  }, [fallbackLatitude, fallbackLongitude, latitude, longitude])

  const initialCoordinatesRef = useRef(resolvedCoordinates)
  const gpsAreasRef = useRef<GpsAreaBounds[]>(gpsAreas ?? [])

  const streetLayerUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
  const satelliteLayerUrl =
    'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'

  const syncCommittedRectangles = useCallback((areasToRender: GpsAreaBounds[]) => {
    const map = mapRef.current
    if (!map) {
      return
    }

    committedRectanglesRef.current.forEach((rectangle) => rectangle.remove())
    committedRectanglesRef.current = areasToRender.map((area) =>
      L.rectangle(fromBoundsObject(area), FINAL_RECTANGLE_STYLE).addTo(map),
    )
  }, [])

  const emitSelection = useCallback((nextAreas: GpsAreaBounds[]) => {
    gpsAreasRef.current = nextAreas
    const markerPosition = markerRef.current?.getLatLng()
    const center = markerPosition ?? L.latLng(resolvedCoordinates.latitude, resolvedCoordinates.longitude)

    onChangeRef.current?.({
      latitude: Number(center.lat.toFixed(6)),
      longitude: Number(center.lng.toFixed(6)),
      gpsAreas: nextAreas,
    })
  }, [resolvedCoordinates.latitude, resolvedCoordinates.longitude])

  const clearDrawnAreas = () => {
    draftRectangleRef.current?.remove()
    draftRectangleRef.current = null
    dragStartRef.current = null
    syncCommittedRectangles([])
    emitSelection([])
  }

  const syncInteractionMode = useCallback(() => {
    const map = mapRef.current
    if (!map) {
      return
    }

    const isDrawingMode = canInteractRef.current && selectModeRef.current
    const container = map.getContainer()

    if (isDrawingMode) {
      map.dragging.disable()
      container.style.cursor = 'crosshair'
    } else {
      map.dragging.enable()
      container.style.cursor = canInteractRef.current ? 'grab' : 'default'
    }
  }, [])

  useEffect(() => {
    canInteractRef.current = canInteract
    syncInteractionMode()
  }, [canInteract, syncInteractionMode])

  useEffect(() => {
    selectModeRef.current = isSelectMode
    syncInteractionMode()

    if (!isSelectMode) {
      dragStartRef.current = null
      draftRectangleRef.current?.remove()
      draftRectangleRef.current = null
    }
  }, [isSelectMode, syncInteractionMode])

  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  useEffect(() => {
    if (!mapElementRef.current || mapRef.current) {
      return
    }

    const initialCoordinates = initialCoordinatesRef.current
    const center: L.LatLngExpression = [initialCoordinates.latitude, initialCoordinates.longitude]

    const map = L.map(mapElementRef.current, {
      zoomControl: true,
      attributionControl: true,
      dragging: true,
      doubleClickZoom: false,
      boxZoom: false,
      scrollWheelZoom: true,
    }).setView(center, DEFAULT_ZOOM)

    const initialLayer = L.tileLayer(satelliteLayerUrl, {
      attribution: '&copy; OpenStreetMap contributors &copy; Esri',
      maxZoom: 19,
    })
    initialLayer.addTo(map)

    layerRef.current = initialLayer
    mapRef.current = map
    syncInteractionMode()

    markerRef.current = L.marker(center, {
      draggable: false,
      icon: createMarkerIcon(),
      interactive: false,
    }).addTo(map)
    if (markerLabel?.trim()) {
      markerRef.current.bindTooltip(markerLabel.trim(), {
        permanent: true,
        direction: 'top',
        offset: [0, -14],
        className: 'gps-picker-label',
      })
    }

    syncCommittedRectangles(gpsAreasRef.current)

    const handleMouseDown = (event: L.LeafletMouseEvent) => {
      if (!canInteractRef.current || !selectModeRef.current) {
        return
      }

      dragStartRef.current = event.latlng
      draftRectangleRef.current?.remove()
      draftRectangleRef.current = L.rectangle(L.latLngBounds(event.latlng, event.latlng), DRAFT_RECTANGLE_STYLE).addTo(map)
    }

    const handleMouseMove = (event: L.LeafletMouseEvent) => {
      if (!dragStartRef.current || !canInteractRef.current || !selectModeRef.current) {
        return
      }

      const bounds = L.latLngBounds(dragStartRef.current, event.latlng)
      if (!draftRectangleRef.current) {
        draftRectangleRef.current = L.rectangle(bounds, DRAFT_RECTANGLE_STYLE).addTo(map)
      } else {
        draftRectangleRef.current.setBounds(bounds)
      }
    }

    const finalizeSelection = (latlng: L.LatLng) => {
      const start = dragStartRef.current
      dragStartRef.current = null
      if (!start || !canInteractRef.current || !selectModeRef.current) {
        draftRectangleRef.current?.remove()
        draftRectangleRef.current = null
        return
      }

      const bounds = L.latLngBounds(start, latlng)
      draftRectangleRef.current?.remove()
      draftRectangleRef.current = null

      if (isTinyBounds(bounds)) {
        return
      }

      const nextAreas = [...gpsAreasRef.current, toBoundsObject(bounds)]
      syncCommittedRectangles(nextAreas)
      emitSelection(nextAreas)
    }

    map.on('mousedown', handleMouseDown)
    map.on('mousemove', handleMouseMove)
    map.on('mouseup', (event: L.LeafletMouseEvent) => finalizeSelection(event.latlng))

    const resizeTimer = window.setTimeout(() => {
      map.invalidateSize()
    }, 120)

    return () => {
      window.clearTimeout(resizeTimer)
      draftRectangleRef.current?.remove()
      draftRectangleRef.current = null
      committedRectanglesRef.current.forEach((rectangle) => rectangle.remove())
      committedRectanglesRef.current = []
      markerRef.current?.remove()
      markerRef.current = null
      layerRef.current = null
      map.remove()
      mapRef.current = null
    }
  }, [emitSelection, markerLabel, satelliteLayerUrl, syncCommittedRectangles, syncInteractionMode])

  useEffect(() => {
    if (!markerRef.current) {
      return
    }

    const label = markerLabel?.trim()
    if (label) {
      markerRef.current.bindTooltip(label, {
        permanent: true,
        direction: 'top',
        offset: [0, -14],
        className: 'gps-picker-label',
      })
      markerRef.current.openTooltip()
    } else if (markerRef.current.getTooltip()) {
      markerRef.current.unbindTooltip()
    }
  }, [markerLabel])

  useEffect(() => {
    const map = mapRef.current
    const marker = markerRef.current
    if (!map || !marker) {
      return
    }

    const nextCenter = L.latLng(resolvedCoordinates.latitude, resolvedCoordinates.longitude)
    marker.setLatLng(nextCenter)

    if ((gpsAreasRef.current?.length ?? 0) === 0) {
      map.setView(nextCenter, map.getZoom(), { animate: false })
    }
  }, [resolvedCoordinates.latitude, resolvedCoordinates.longitude])

  useEffect(() => {
    if (!mapRef.current || !layerRef.current) {
      return
    }

    const nextUrl = mapViewMode === 'satellite' ? satelliteLayerUrl : streetLayerUrl
    layerRef.current.setUrl(nextUrl)
  }, [mapViewMode])

  useEffect(() => {
    gpsAreasRef.current = gpsAreas ?? []
    syncCommittedRectangles(gpsAreasRef.current)
  }, [gpsAreas, syncCommittedRectangles])

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        {instructionText?.trim() ? (
          <p className="pt-1 text-sm text-muted-foreground">{instructionText.trim()}</p>
        ) : (
          <div />
        )}
        <div className="flex flex-wrap items-center gap-2">
          {canInteract ? (
            <>
              <Button
                type="button"
                size="sm"
                variant={isSelectMode ? 'default' : 'outline'}
                onClick={() => setIsSelectMode((prev) => !prev)}
              >
                <SquareDashedMousePointer className="mr-1 h-4 w-4" />
                Select
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={clearDrawnAreas}>
                <Eraser className="mr-1 h-4 w-4" />
                Clear
              </Button>
            </>
          ) : null}
          <Button
            type="button"
            size="sm"
            variant={mapViewMode === 'street' ? 'default' : 'outline'}
            onClick={() => setMapViewMode('street')}
          >
            Street
          </Button>
          <Button
            type="button"
            size="sm"
            variant={mapViewMode === 'satellite' ? 'default' : 'outline'}
            onClick={() => setMapViewMode('satellite')}
          >
            Satellite
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-md border border-gray-200">
        <div ref={mapElementRef} className="h-[440px] w-full bg-slate-100" />
      </div>
    </div>
  )
}
